import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { authService } from '@/services/auth.service';
import { useToast } from '@/hooks/use-toast';

const DAYS = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
] as const;

const schema = z
  .object({
    full_name: z
      .string()
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name must be less than 100 characters'),
    email: z.string().email('Please enter a valid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm: z.string(),
    daily_hours: z.coerce.number().min(0.5, 'Minimum 0.5 hours').max(24, 'Maximum 24 hours'),
  })
  .refine((data) => data.password === data.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  });

type FormData = z.infer<typeof schema>;

export function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const invitationToken = searchParams.get('invitation');

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [workingDays, setWorkingDays] = useState<string[]>([
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday',
  ]);
  const [invitationInfo, setInvitationInfo] = useState<{
    email: string; workspace_name: string; role: string; inviter_name: string;
  } | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { daily_hours: 8 },
  });

  // Fetch invitation info to show banner and pre-fill email
  useEffect(() => {
    if (!invitationToken) return;
    authService.getInvitationInfo(invitationToken)
      .then((res) => {
        const info = res.data.data;
        setInvitationInfo(info);
        if (info.email) setValue('email', info.email);
      })
      .catch(() => {
        // Token invalid/expired - silently ignore, user can still sign up normally
      });
  }, [invitationToken, setValue]);

  function toggleDay(day: string) {
    setWorkingDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  }

  async function onSubmit(data: FormData) {
    setError('');
    setIsLoading(true);
    try {
      const res = await authService.register({
        full_name: data.full_name,
        email: data.email,
        password: data.password,
        working_days: workingDays,
        daily_hours: data.daily_hours,
        ...(invitationToken && { invitation_token: invitationToken }),
      });

      const result = res.data.data;

      // Invited user: auto-approved, go to login to get tokens
      if (result.approval_status === 'approved' && result.joined_workspace) {
        toast({
          title: 'Welcome!',
          description: `You've been added to ${result.joined_workspace.name}. Please sign in.`,
        });
        navigate('/login', { replace: true });
      } else {
        // Normal signup: pending approval
        navigate('/pending-approval');
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: { message?: string } } } };
      setError(
        axiosError.response?.data?.error?.message || 'Registration failed. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <AuthLayout wide>
      <Card className="border-0 bg-white/80 shadow-lg shadow-purple-500/5 backdrop-blur-sm dark:bg-card/80 rounded-xl">
        <CardContent className="p-6 sm:p-8">
          {/* Header */}
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-brand-primary to-purple-500 bg-clip-text text-transparent">
              Create an account
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Enter your details to get started
            </p>
          </div>

          {/* Invitation banner */}
          {invitationToken && (
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800/50 dark:bg-blue-900/20 dark:text-blue-300">
              {invitationInfo
                ? `🎉 You've been invited to join ${invitationInfo.workspace_name} on DALY! Complete your signup to get started.`
                : '🎉 You\'ve been invited to join a workspace on DALY! Complete your signup to get started.'}
            </div>
          )}

          {/* Error alert */}
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Full Name */}
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                placeholder="John Doe"
                autoComplete="name"
                {...register('full_name')}
              />
              {errors.full_name && (
                <p className="text-xs text-destructive">{errors.full_name.message}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min 8 characters"
                  autoComplete="new-password"
                  className="pr-10"
                  {...register('password')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  className="pr-10"
                  {...register('confirm')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowConfirm(!showConfirm)}
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirm && (
                <p className="text-xs text-destructive">{errors.confirm.message}</p>
              )}
            </div>

            {/* Working Days */}
            <div className="space-y-2">
              <Label>Working Days</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {DAYS.map((day) => (
                  <label
                    key={day.key}
                    className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                  >
                    <Checkbox
                      checked={workingDays.includes(day.key)}
                      onCheckedChange={() => toggleDay(day.key)}
                    />
                    <span className="font-medium">{day.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Daily Hours */}
            <div className="space-y-2">
              <Label htmlFor="daily_hours">Daily Hours</Label>
              <Input
                id="daily_hours"
                type="number"
                step="0.5"
                min="0.5"
                max="24"
                {...register('daily_hours')}
              />
              <p className="text-xs text-muted-foreground">Hours you work per day</p>
              {errors.daily_hours && (
                <p className="text-xs text-destructive">{errors.daily_hours.message}</p>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="h-10 w-full rounded-md bg-gradient-to-r from-brand-primary to-purple-600 text-white shadow-md hover:shadow-lg hover:opacity-90 transition-all"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </Button>
          </form>

          {/* Footer links */}
          <div className="mt-6 space-y-3 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-brand-primary hover:text-brand-primary/80 hover:underline"
              >
                Sign in
              </Link>
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to home
            </Link>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
